import React, { useEffect, useState } from 'react';
import { ArrowRightLeft, X, Loader2 } from 'lucide-react';
import { useApi } from '../../hooks/useApi';

const PRESENCE = {
  online: { label: 'Disponible', color: 'var(--success)' },
  paused: { label: 'En pausa', color: 'var(--warning)' },
  offline: { label: 'Desconectado', color: 'var(--text-3)' },
};

/**
 * Transferir un caso a un compañero (punto 1 del cliente). Aparecen todos los
 * que tienen "Recibe casos", agrupados por tipo (también los que solo reciben
 * transferencias); en pausa o desconectados también: lo atienden al volver.
 * La nota es opcional y la ve quien recibe el caso.
 */
/** [[nombre del tipo, compañeros], ...]; dentro de cada tipo se respeta el orden del backend. */
function groupByType(team) {
  const groups = new Map();
  for (const u of [...team].sort((a, b) => a.agent_type.localeCompare(b.agent_type))) {
    const label = u.agent_type_label || u.agent_type;
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(u);
  }
  return [...groups.entries()];
}

export default function TransferModal({ conv, currentAssigneeId, onClose, onTransfer }) {
  const { apiFetch } = useApi();
  const [team, setTeam] = useState(null);
  const [error, setError] = useState('');
  const [toUser, setToUser] = useState(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    apiFetch('/api/team')
      .then(rows => setTeam(rows.filter(u => u.id !== currentAssigneeId)))
      .catch(e => setError(e.message));
  }, [apiFetch, currentAssigneeId]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    if (!toUser) return;
    setSending(true);
    setError('');
    try {
      await onTransfer(conv.session_id, toUser, note.trim());
      onClose();
    } catch (err) {
      setError(err.message);
      setSending(false);
    }
  }

  return (
    <div style={st.overlay} onClick={onClose}>
      <form style={st.modal} onClick={e => e.stopPropagation()} onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="transfer-title">
        <div style={st.head}>
          <ArrowRightLeft size={18} color="var(--accent)" />
          <h2 id="transfer-title" style={st.title}>Transferir caso</h2>
          <button type="button" style={st.close} onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        </div>
        <p style={st.sub}>{conv.contact_name ? `${conv.contact_name} · ` : ''}{conv.user_id}. Le llega de inmediato a quien elijas, aunque esté en pausa o desconectado.</p>

        <div style={st.label}>Pasar a</div>
        {!team && !error && <div style={st.muted}>Cargando…</div>}
        {team && team.length === 0 && <div style={st.muted}>No hay compañeros con "Recibe casos" activo.</div>}
        <div style={st.list} role="radiogroup" aria-label="Compañeros">
          {team && groupByType(team).map(([label, members]) => [
            <div key={`g-${label}`} style={st.group}>{label}</div>,
            ...members.map(u => {
            const p = PRESENCE[u.presence] || PRESENCE.offline;
            const sel = toUser === u.id;
            return (
              <label key={u.id} style={{ ...st.option, ...(sel ? st.optionSel : {}) }}>
                <input type="radio" name="to" checked={sel} onChange={() => setToUser(u.id)} style={{ accentColor: 'var(--accent)' }} />
                <span style={{ flex: 1 }}>
                  <strong>{u.name}</strong>{u.role === 'admin' ? <span style={st.muted}> · admin</span> : null}
                  <span style={{ display: 'block', fontSize: 12, color: p.color }}>● {p.label} · <span style={st.muted}>{u.open_cases} {u.open_cases === 1 ? 'caso' : 'casos'}</span></span>
                </span>
              </label>
            );
            }),
          ])}
        </div>

        <label style={st.label} htmlFor="transfer-note">Nota para tu compañero (opcional)</label>
        <textarea id="transfer-note" style={st.textarea} rows={3} maxLength={500} value={note} onChange={e => setNote(e.target.value)}
          placeholder="Ej.: quiere visitar la sede el sábado; ya le envié precios." />

        {error && <div style={st.error} role="alert">{error}</div>}
        <div style={st.actions}>
          <button type="button" className="ui-hover" style={st.secondary} onClick={onClose}>Cancelar</button>
          <button type="submit" className="ui-primary" style={st.primary} disabled={!toUser || sending}>
            {sending ? <Loader2 size={15} className="ui-spin" /> : <ArrowRightLeft size={15} />} Transferir
          </button>
        </div>
      </form>
    </div>
  );
}

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 },
  modal: { background: 'var(--raised)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 460, padding: 20, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '90vh', overflowY: 'auto' },
  head: { display: 'flex', alignItems: 'center', gap: 10 },
  title: { fontSize: 17, fontWeight: 800, margin: 0, flex: 1, color: 'var(--text)' },
  close: { background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', display: 'flex', padding: 4 },
  sub: { fontSize: 13, color: 'var(--text-2)', margin: 0 },
  label: { fontSize: 12, fontWeight: 700, color: 'var(--text-2)', marginTop: 4 },
  muted: { color: 'var(--text-3)', fontSize: 12.5 },
  group: { fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.4, margin: '6px 2px 0' },
  list: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' },
  option: { display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontSize: 13.5, color: 'var(--text)', background: 'var(--surface)' },
  optionSel: { borderColor: 'var(--accent)', background: 'var(--accent-soft)' },
  textarea: { width: '100%', background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '8px 10px', color: 'var(--text)', fontSize: 13.5, resize: 'vertical' },
  error: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', color: 'var(--danger)', borderRadius: 10, padding: '8px 12px', fontSize: 13 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 },
  secondary: { background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, color: 'var(--text)', cursor: 'pointer' },
  primary: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent)', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 700, color: 'var(--on-accent)', cursor: 'pointer' },
};
